import { useState, useEffect } from 'react';
import LegalLayout from './LegalLayout';

function TermosUso() {
  const [html, setHtml] = useState<string | null>(null);
  const [css, setCss] = useState('');

  useEffect(() => {
    fetch('/api/conteudo/pagina/termos')
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((data) => {
        if (data.html && data.html.trim()) {
          setHtml(data.html);
          setCss(data.css || '');
        }
      })
      .catch(() => {});
  }, []);

  if (html) {
    return (
      <LegalLayout>
        <style dangerouslySetInnerHTML={{ __html: css }} />
        <div dangerouslySetInnerHTML={{ __html: html }} />
      </LegalLayout>
    );
  }

  return (
    <LegalLayout>
      <h1>Termos de Uso</h1>
      <p className="legal-last-updated">Última atualização: Janeiro de 2025</p>

      <p>
        Estes Termos de Uso regulam o acesso e a utilização da plataforma de acompanhamento de
        obras da <strong>Vavassori Designer</strong>. Ao acessar ou utilizar nossos serviços, você
        concorda com estes termos.
      </p>

      <h2>1. Descrição do Serviço</h2>
      <p>
        A plataforma Vavassori Designer é um sistema de gerenciamento e acompanhamento de projetos
        de construção civil que permite:
      </p>
      <ul>
        <li>Acompanhamento em tempo real do progresso de obras</li>
        <li>Registro fotográfico detalhado de cada etapa construtiva</li>
        <li>Gerenciamento de cronogramas e atividades</li>
        <li>Comunicação entre clientes, arquitetos e equipe técnica</li>
        <li>Documentação centralizada do projeto</li>
        <li>Relatórios de progresso e apontamentos</li>
      </ul>

      <h2>2. Cadastro e Acesso</h2>
      <p>
        O acesso à plataforma é concedido mediante cadastro realizado pela equipe da Vavassori
        Designer. Cada usuário recebe credenciais únicas de acordo com seu perfil (Cliente,
        Arquiteto ou Assistente).
      </p>

      <h2>3. Responsabilidades do Usuário</h2>
      <p>Ao utilizar a plataforma, o usuário se compromete a:</p>
      <ul>
        <li>
          Manter suas credenciais de acesso em sigilo e não compartilhá-las com terceiros
        </li>
        <li>
          Utilizar a plataforma exclusivamente para fins relacionados ao projeto contratado
        </li>
        <li>Realizar upload apenas de fotos e documentos relevantes à obra</li>
        <li>Não tentar acessar dados de projetos ou áreas não autorizadas</li>
        <li>Comunicar imediatamente qualquer uso não autorizado de sua conta</li>
        <li>Não utilizar a plataforma para fins ilegais ou não autorizados</li>
        <li>Respeitar os direitos de propriedade intelectual de terceiros</li>
      </ul>

      <h2>4. Responsabilidades da Plataforma</h2>
      <p>A Vavassori Designer se compromete a:</p>
      <ul>
        <li>Manter a segurança e integridade dos dados armazenados</li>
        <li>Garantir a disponibilidade do serviço, ressalvadas manutenções programadas</li>
        <li>Proteger os dados pessoais conforme a LGPD e nossa Política de Privacidade</li>
        <li>Realizar backups regulares para proteção contra perda de dados</li>
        <li>Notificar os usuários sobre manutenções programadas com antecedência</li>
        <li>Fornecer suporte técnico para resolução de problemas de acesso</li>
      </ul>

      <h2>5. Propriedade Intelectual</h2>
      <p>
        A plataforma, incluindo seu código-fonte, design, interface, textos e funcionalidades, é
        propriedade exclusiva da Vavassori Designer e está protegida pelas leis de propriedade
        intelectual brasileiras.
      </p>
      <p>
        Os conteúdos enviados pelos usuários (fotos, documentos) permanecem de propriedade do
        respectivo titular. Ao fazer upload, o usuário concede à plataforma uma licença limitada
        para armazenamento, exibição e backup dos materiais no contexto do projeto.
      </p>
      <p>
        É proibida a reprodução, distribuição ou modificação de qualquer parte da plataforma sem
        autorização prévia por escrito.
      </p>

      <h2>6. Disponibilidade do Serviço</h2>
      <p>
        A plataforma é disponibilizada "como está" e buscamos manter alta disponibilidade. No
        entanto, não garantimos funcionamento ininterrupto. Podem ocorrer interrupções por:
      </p>
      <ul>
        <li>Manutenções preventivas e corretivas (com aviso prévio quando possível)</li>
        <li>Atualizações de segurança</li>
        <li>Eventos de força maior</li>
        <li>Problemas técnicos fora do nosso controle</li>
      </ul>

      <h2>7. Limitação de Responsabilidade</h2>
      <p>A Vavassori Designer não se responsabiliza por:</p>
      <ul>
        <li>Danos decorrentes de uso indevido da plataforma pelo usuário</li>
        <li>Perdas causadas por compartilhamento de credenciais de acesso</li>
        <li>Indisponibilidade temporária por manutenção ou força maior</li>
        <li>Conteúdos enviados por terceiros na plataforma</li>
        <li>Decisões tomadas com base nas informações exibidas na plataforma</li>
      </ul>
      <p>
        A responsabilidade total da Vavassori Designer, em qualquer hipótese, será limitada ao
        valor pago pelo serviço no período de 12 meses anterior ao evento.
      </p>

      <h2>8. Encerramento de Conta</h2>
      <p>A conta do usuário pode ser encerrada:</p>
      <ul>
        <li>
          <strong>Pelo usuário:</strong> mediante solicitação por escrito
        </li>
        <li>
          <strong>Pela plataforma:</strong> em caso de violação destes termos, uso indevido ou
          encerramento do contrato de prestação de serviços
        </li>
        <li>
          <strong>Por conclusão do projeto:</strong> ao término do projeto, o acesso é mantido por
          período adicional para consulta ao histórico
        </li>
      </ul>
      <p>
        Após o encerramento, os dados serão tratados conforme a Política de Privacidade e a
        legislação aplicável.
      </p>

      <h2>9. Modificações dos Termos</h2>
      <p>
        Reservamo-nos o direito de modificar estes termos a qualquer momento. Alterações
        significativas serão comunicadas aos usuários com antecedência mínima de 30 dias. O uso
        continuado da plataforma após as alterações constitui aceite dos novos termos.
      </p>

      <h2>10. Legislação Aplicável</h2>
      <p>
        Estes termos são regidos pela legislação brasileira. Fica eleito o foro da comarca de
        Joinville/SC para dirimir quaisquer controvérsias decorrentes destes termos.
      </p>

      <h2>11. Contato</h2>
      <p>Para dúvidas sobre estes Termos de Uso, entre em contato:</p>
      <p>
        <strong>E-mail:</strong>{' '}
        <a href="mailto:contato@vavassoridesigner.com.br">contato@vavassoridesigner.com.br</a>
      </p>
      <p>
        <strong>Empresa:</strong> Vavassori Designer — Joinville, SC
      </p>
    </LegalLayout>
  );
}

export default TermosUso;
